import { useState, useEffect, useRef } from "react";
import { Cpu, BarChart3, Zap, Globe, Database, Radio, X, Maximize2 } from "lucide-react";

const ICONS = [Cpu, BarChart3, Zap, Globe, Database, Radio];

const randomBetween = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const generateFakeLog = () => {
  const logs = [
    "SCANNING SECTOR 7G...",
    "ENCRYPTING CHANNEL 4.2",
    "TRACE: 0xAF29 OK",
    "FIREWALL STATUS: ACTIVE",
    "BANDWIDTH: 94.2 GB/s",
    "NODE SYNC: COMPLETE",
    "UPLINK: STABLE",
    "DECRYPTION: PASS",
    "CORE TEMP: 42.1C",
    "SHIELD INTEGRITY: 98%",
    "QUANTUM LINK: ACTIVE",
    "MEM ALLOC: 0x7FFE",
    "RADAR SWEEP: CLEAR",
    "PROTOCOL 7: ENGAGED",
    "SATELLITE LOCK: 4/4",
    "THREAT LEVEL: ZERO",
  ];
  return logs[Math.floor(Math.random() * logs.length)];
};

/* ---------- single metric bar ---------- */
const MetricBar = ({ label, value, max = 100, delay = 0, color = "cyan", expanded = false }) => {
  const pct = Math.min((value / max) * 100, 100);
  const gradients = {
    cyan: "from-cyan-500 to-cyan-300",
    amber: "from-amber-500 to-amber-300",
    emerald: "from-emerald-500 to-emerald-300",
    red: "from-red-500 to-red-400",
  };
  const textColors = {
    cyan: "text-cyan-400",
    amber: "text-amber-400",
    emerald: "text-emerald-400",
    red: "text-red-400",
  };
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex justify-between font-mono text-[9px] tracking-wider">
        <span className="text-cyan-600">{label}</span>
        <span className={`${textColors[color]} number-tick`}>{value.toFixed(1)}%</span>
      </div>
      <div className={`${expanded ? "h-[5px]" : "h-[3px]"} w-full bg-cyan-950/50 rounded-full overflow-hidden transition-all duration-300`}>
        <div
          className={`h-full bg-gradient-to-r ${gradients[color]} rounded-full transition-all duration-700`}
          style={{ width: `${pct}%`, animationDelay: `${delay}s` }}
        />
      </div>
      {expanded && (
        <div className="font-mono text-[7px] text-cyan-600/60 tracking-wider">
          MIN: {(value * 0.6).toFixed(1)} / MAX: {Math.min(value * 1.15, 100).toFixed(1)} / AVG: {(value * 0.9).toFixed(1)}
        </div>
      )}
    </div>
  );
};

/* ---------- command log ---------- */
const CommandLog = () => {
  const [logs, setLogs] = useState(() =>
    Array.from({ length: 8 }, () => generateFakeLog())
  );
  const logRef = useRef(null);

  useEffect(() => {
    const iv = setInterval(() => {
      setLogs((prev) => [...prev.slice(-11), generateFakeLog()]);
    }, 8000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (logRef.current)
      logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logs]);

  return (
    <div
      ref={logRef}
      className="flex flex-col gap-[2px] h-[80px] overflow-hidden font-mono text-[8px] md:text-[9px] leading-relaxed tracking-wider text-cyan-500/60"
    >
      {logs.map((l, i) => (
        <div key={i} className={i === logs.length - 1 ? "text-cyan-300" : ""}>
          &gt; {l}
        </div>
      ))}
    </div>
  );
};

/* ---------- radar widget ---------- */
const RadarWidget = () => (
  <div className="relative w-full aspect-square max-w-[100px] mx-auto rounded-full border border-cyan-500/15 overflow-hidden bg-gray-950/50">
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-full h-[1px] bg-cyan-500/10" />
    </div>
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="h-full w-[1px] bg-cyan-500/10" />
    </div>
    <div className="absolute inset-[33%] rounded-full border border-cyan-500/8" />
    <div className="absolute inset-[50%] w-1.5 h-1.5 bg-cyan-400/50 rounded-full" />
  </div>
);

/* ---------- expanded detail panel ---------- */
const ExpandedDetail = ({ side, metrics, onClose }) => (
  <div
    data-testid={`expanded-widget-${side}`}
    className="flex flex-col gap-1.5 bg-gray-950/85 border border-cyan-500/25 rounded p-3"
    style={{ cursor: "default" }}
  >
    <div className="flex items-center justify-between border-b border-cyan-500/20 pb-2">
      <span className="font-heading text-xs tracking-[0.2em] uppercase text-cyan-300">
        {side === "left" ? "DETAILED SYSTEM ANALYSIS" : "NETWORK DIAGNOSTICS"}
      </span>
      <button
        data-testid={`close-expanded-${side}`}
        onClick={onClose}
        className="text-cyan-500 hover:text-cyan-300 cursor-pointer transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>

    {side === "left" ? (
      <>
        <MetricBar label="CPU USAGE" value={metrics.cpu} color="cyan" expanded />
        <MetricBar label="MEMORY" value={metrics.mem} color="cyan" expanded />
        <MetricBar label="NETWORK I/O" value={metrics.net} color="amber" expanded />
        <MetricBar label="POWER GRID" value={metrics.pwr} color="emerald" expanded />
        <MetricBar label="SHIELD MATRIX" value={metrics.shield} color={metrics.shield > 95 ? "emerald" : "red"} expanded />
        <div className="grid grid-cols-2 gap-2 mt-1 font-mono text-[7px] tracking-wider">
          <div className="text-cyan-600">THREADS: <span className="text-cyan-400">2,048</span></div>
          <div className="text-cyan-600">CACHE: <span className="text-amber-400">98.2% HIT</span></div>
          <div className="text-cyan-600">UPTIME: <span className="text-emerald-400">847h 23m</span></div>
          <div className="text-cyan-600">ERRORS: <span className="text-emerald-400">0</span></div>
        </div>
        <CommandLog />
      </>
    ) : (
      <>
        <RadarWidget />
        <MetricBar label="UPLINK STRENGTH" value={metrics.net} color="emerald" expanded />
        <MetricBar label="BANDWIDTH UTIL" value={metrics.cpu} color="cyan" expanded />
        <MetricBar label="PING LATENCY" value={100 - metrics.mem} color="amber" expanded />
        <div className="grid grid-cols-2 gap-2 mt-1 font-mono text-[7px] tracking-wider">
          <div className="text-cyan-600">NODES: <span className="text-cyan-400">14 ACTIVE</span></div>
          <div className="text-cyan-600">PACKETS: <span className="text-emerald-400">0 LOST</span></div>
          <div className="text-cyan-600">ENCRYPT: <span className="text-amber-400">AES-256</span></div>
          <div className="text-cyan-600">PROTOCOL: <span className="text-cyan-400">TCP/Q7</span></div>
        </div>
        <CommandLog />
      </>
    )}
  </div>
);

/* ---------- main widget ---------- */
const DataWidget = ({ side = "left", className = "" }) => {
  const [metrics, setMetrics] = useState({
    cpu: 67.3,
    mem: 42.1,
    net: 88.9,
    pwr: 95.2,
    shield: 98.0,
  });
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const iv = setInterval(() => {
      setMetrics({
        cpu: randomBetween(40, 99) + Math.random(),
        mem: randomBetween(30, 80) + Math.random(),
        net: randomBetween(60, 99) + Math.random(),
        pwr: randomBetween(85, 100) + Math.random(),
        shield: randomBetween(90, 100) + Math.random(),
      });
    }, 12000);
    return () => clearInterval(iv);
  }, []);

  const Icon = ICONS[side === "left" ? 0 : 3];

  if (expanded) {
    return <ExpandedDetail side={side} metrics={metrics} onClose={() => setExpanded(false)} />;
  }

  return (
    <div
      data-testid={`data-widget-${side}`}
      className={`flex flex-col gap-2 bg-gray-950/80 border border-cyan-500/20 rounded-sm p-3 cursor-pointer transition-colors duration-300 hover:border-cyan-400/30 hover:bg-gray-950/85 ${className}`}
      onClick={() => setExpanded(true)}
      style={{ cursor: "pointer" }}
    >
      {/* Widget header */}
      <div className="flex items-center justify-between border-b border-cyan-500/15 pb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-cyan-400" />
          <span className="font-heading text-[10px] md:text-xs tracking-[0.2em] uppercase text-cyan-300">
            {side === "left" ? "SYSTEM METRICS" : "NETWORK STATUS"}
          </span>
        </div>
        <Maximize2 className="w-3 h-3 text-cyan-600/50" />
      </div>

      {/* Content differs per side */}
      {side === "left" ? (
        <>
          <MetricBar label="CPU USAGE" value={metrics.cpu} delay={0} color="cyan" />
          <MetricBar label="MEMORY" value={metrics.mem} delay={0.5} color="cyan" />
          <MetricBar label="NETWORK" value={metrics.net} delay={1} color="amber" />
          <MetricBar label="POWER" value={metrics.pwr} delay={1.5} color="emerald" />
          <MetricBar label="SHIELD" value={metrics.shield} delay={2} color={metrics.shield > 95 ? "emerald" : "red"} />
          <div className="mt-1">
            <CommandLog />
          </div>
        </>
      ) : (
        <>
          <RadarWidget />
          <MetricBar label="UPLINK" value={metrics.net} delay={0} color="emerald" />
          <MetricBar label="BANDWIDTH" value={metrics.cpu} delay={0.6} color="cyan" />
          <MetricBar label="LATENCY" value={100 - metrics.mem} delay={1.2} color="amber" />
          <div className="mt-1">
            <CommandLog />
          </div>
        </>
      )}
    </div>
  );
};

export default DataWidget;
